import {
    createBaseQueries,
    transformErrorResponse,
    transformSuccessResponse,
    transformRequest,
    EXTERNAL_URL
} from '@/services';
import { createApi } from '@reduxjs/toolkit/query/react';

const { rawExternalApiBaseQuery: profileBaseQuery } = createBaseQueries({
    externalURLType: EXTERNAL_URL.sregService,
});

const profileAPI = createApi({
    reducerPath: 'profileAPI',
    baseQuery: profileBaseQuery,
    tagTypes: ['ProfileDashboard'],
    endpoints: (builder) => ({
        // GET — fetch profile dashboard data
        getProfileDashboard: builder.query({
            query: () => ({
                url: `/student/profile/dashboard`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            providesTags: ['ProfileDashboard'],
        }),

        // POST — search academic details by seat/enrollment number
        searchAcademicDetails: builder.mutation({
            query: (requestData) => ({
                url: `/student/profile/academic-details`,
                method: 'POST',
                body: transformRequest(requestData),
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        // POST — save academic details
        saveAcademicDetails: builder.mutation({
            query: (requestData) => ({
                url: `/student/profile/academic-details`,
                method: 'POST',
                body: transformRequest(requestData),
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        // PUT — save/update student profile (e.g. academic history ssc/hsc records)
        saveProfile: builder.mutation({
            query: (requestData) => ({
                url: `/student/profile`,
                method: 'PUT',
                body: transformRequest(requestData),
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            invalidatesTags: ['ProfileDashboard'],
        }),

        // GET — register/fetch a new academic record shell for the selected education type
        registerAcademicRecord: builder.query({
            query: (params) => ({
                url: `/student/register/academic-record`,
                method: 'GET',
                params,
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        // GET — fetch all student social media profile links (shared across portals)
        getSocialProfileLink: builder.query({
            query: () => ({
                url: `/student/profile/social-link`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            providesTags: ['ProfileDashboard'],
        }),

        // GET — fetch a single student social media profile link by id
        getByIdSocialProfileLink: builder.query({
            query: (id) => ({
                url: `/student/profile/social-link/${id}`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            providesTags: ['ProfileDashboard'],
        }),

        // POST — add a student social media profile link (shared across portals)
        addSocialProfileLink: builder.mutation({
            query: (params) => ({
                url: `/student/profile/social-link`,
                method: 'POST',
                params,
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            invalidatesTags: ['ProfileDashboard'],
        }),

        // PUT — update a student social media profile link (params: urlId, name, url)
        updateSocialProfileLink: builder.mutation({
            query: (params) => ({
                url: `/student/profile/social-link`,
                method: 'PUT',
                params,
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            invalidatesTags: ['ProfileDashboard'],
        }),

        // DELETE — remove a student social media profile link
        deleteSocialProfileLink: builder.mutation({
            query: ({ id}) => ({
                url: `/student/profile/social-link/${id}`,
                method: 'DELETE',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            invalidatesTags: ['ProfileDashboard'],
        }),

    }),
});

export const {
    useGetProfileDashboardQuery,
    useSearchAcademicDetailsMutation,
    useSaveAcademicDetailsMutation,
    useSaveProfileMutation,
    useLazyRegisterAcademicRecordQuery,
    useGetSocialProfileLinkQuery,
    useLazyGetSocialProfileLinkQuery,
    useGetByIdSocialProfileLinkQuery,
    useLazyGetByIdSocialProfileLinkQuery,
    useAddSocialProfileLinkMutation,
    useUpdateSocialProfileLinkMutation,
    useDeleteSocialProfileLinkMutation,
} = profileAPI;

export default profileAPI;
